import { requestType } from "./requests";

/**
 * The two levels Content Management can edit at.
 *
 * - `AGENCY` — one travel agency's own content, keyed by `agency_id`
 *   (= `selectedBrand.uid` = `agency_id_no_r`). The original behaviour.
 * - `COUNTRY` — every agency in a market at once, keyed by `market_code`
 *   (= `selectedCountry.countryCode`, one of the codes in `utilities/countries.js`).
 *   The backend fans the write out to the country's agencies.
 *
 * Which level a page is editing at comes from the URL — see
 * `useContentManagementScope` — so it survives a refresh and a pasted link.
 */
export const CONTENT_MANAGEMENT_LEVELS = {
    AGENCY: "agency",
    COUNTRY: "country",
};

/**
 * Confirmed against the SET_BRIEF_*_V2 docs (Teamwork 41791069).
 *
 * The country level does reuse the agency level's endpoints — that half of the
 * earlier guess was right. What was wrong: it is not a swap of one key for
 * another. The wire contract is a `scope` discriminator plus the key that scope
 * is addressed by:
 *
 * - `scope: "agency"` (the backend's default) + `agency_id`
 * - `scope: "market"` + `market_code`; `agency_id` is not read at all
 *
 * So the payload gained a field rather than renaming one, and the key is
 * `market_code`, not `country_code`. The *values* were already right:
 * `selectedCountry.countryCode` comes from `COUNTRIES` in `utilities/countries.js`,
 * whose codes (`UK`, `IE`, `NORDICS`, `MT`, `AE`, `ES`) are exactly the set the
 * backend accepts (`General::getMarketBitMap()`); anything else is
 * `ERROR_INVALID_MARKET_CODE`.
 *
 * Nothing else in the app should spell any of these out.
 */
export const AGENCY_SCOPE_KEY = "agency_id";
export const MARKET_SCOPE_KEY = "market_code";

/** The `scope` values the V2 write endpoints accept. Anything else is `ERROR_INVALID_CONTENT_SAVE_SCOPE`. */
export const CONTENT_SAVE_SCOPES = {
    AGENCY: "agency",
    MARKET: "market",
};

/**
 * The scope fields to merge into a **write** payload.
 *
 * Covers both write families, which now share one contract:
 *
 * - `SET_BRIEF_*_V2` — the four Content Management sections.
 * - `CREATE`/`UPDATE_AGENCY_SHIP` and `CREATE`/`UPDATE_AGENCY_DESTINATION` — the
 *   ship and destination customisation writes. These used to send a shape of
 *   their own through a `buildLegacyScopeData` helper, because they had no
 *   documented market level and guessing one would have changed endpoints that
 *   worked. They have since gained it, documented identically to the brief
 *   writes: `scope` optional and defaulting to `"agency"`, `market_code`
 *   required when `scope: "market"`, and `agency_id` **omitted** for a market
 *   save. That is exactly what this builder produces, so the helper is gone.
 *
 * `scope` is sent explicitly at both levels even though the backend defaults to
 * `"agency"`: the default is what silently swallowed the country level before
 * this was corrected — a payload with no `scope` and no `agency_id` is read as a
 * malformed agency save, not as a market save.
 *
 * ⚠️ Writes only. The matching **reads** (`GET_AGENCY_SHIP_DETAILS`,
 * `GET_AGENCY_SHIPS`, `GET_AGENCY_DESTINATIONS`) take neither `scope` nor
 * `market_code` — they require `agency_id` and resolve the market from it. Use
 * `buildReadScopeData` there, never this.
 *
 * Returns an empty object when the scope has no value yet (no brand or no market
 * selected), so a caller can spread it unconditionally — the guard belongs with
 * `isScopeReady` at the call site, not scattered through payload builders.
 */
export const buildScopeData = (scope) => {
    if (!scope) return {};
    if (scope.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY) {
        return scope.countryCode
            ? { scope: CONTENT_SAVE_SCOPES.MARKET, [MARKET_SCOPE_KEY]: scope.countryCode }
            : {};
    }
    return scope.agencyId
        ? { scope: CONTENT_SAVE_SCOPES.AGENCY, [AGENCY_SCOPE_KEY]: scope.agencyId }
        : {};
};

/**
 * True when the current scope's content can actually be fetched.
 *
 * ## History: the country level used to be write-only
 *
 * All four `GET_BRIEF_*_V2` endpoints originally took `market_code` as a
 * *response* field only — the market a read had *fallen back to* — never as an
 * input. Their one input was `agency_id` (explicit, or resolved implicitly from
 * `user_uid`), resolving `agency record → that agency's market record → system
 * default`, so market content was reachable only *through* an agency and there
 * was no way to ask "what is saved for market UK".
 *
 * A market-level read was therefore reported as unsupported behind a
 * `MARKET_LEVEL_READ_SUPPORTED` flag rather than attempted, because sending one
 * with no `agency_id` either failed with `ERROR_MISSING_REQUIRED_FIELDS` or —
 * worse — fell through to `_resolveImplicitAgencyId(user_uid)` and quietly
 * answered with the *signed-in user's own agency's* content while the page was
 * captioned as the market's. The save bar would then have offered to publish that
 * one agency's content to every agency in the market.
 *
 * The backend now accepts `market_code` as a read input on all four, so the flag
 * is gone and the country level reads its own content.
 *
 * ⚠️ This covers the **brief** reads only. The ship and destination reads
 * (`GET_AGENCY_SHIP_DETAILS`, `GET_AGENCY_SHIPS`, `GET_AGENCY_DESTINATIONS`) took
 * no market input when last checked against their Postman docs: each requires
 * `agency_id` and resolves the market from it, so they have no market-level read
 * of their own even though their writes now take `scope: "market"`.
 *
 * ## Why this still exists alongside `isScopeReady`
 *
 * The two now return the same answer for every scope — each level is readable
 * exactly when it has the value it is keyed by. They are kept apart because they
 * answer different questions ("can I fetch this?" vs "can I write this?"), and
 * those came apart once already. If a future endpoint is again write-only at one
 * level, this is the single place that has to say so.
 */
export const isReadSupported = (scope) =>
    scope?.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY
        ? !!scope.countryCode
        : !!scope?.agencyId;

/**
 * The scope fields to merge into a **read** payload (`GET_BRIEF_*_V2`).
 *
 * Deliberately not the same as `buildScopeData`: a read takes no `scope`
 * discriminator — just the key its level is addressed by, `market_code` at
 * country level and `agency_id` at agency level.
 *
 * Returns `{}` when the scope has no value yet, which would read as another
 * scope's content; guard with `isReadSupported` before firing rather than sending
 * this blind.
 */
export const buildReadScopeData = (scope) => {
    if (!scope) return {};
    if (scope.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY) {
        return scope.countryCode ? { [MARKET_SCOPE_KEY]: scope.countryCode } : {};
    }
    return scope.agencyId ? { [AGENCY_SCOPE_KEY]: scope.agencyId } : {};
};

/** True once the scope has the value its level is keyed by. */
export const isScopeReady = (scope) =>
    scope?.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY ? !!scope.countryCode : !!scope?.agencyId;

/**
 * A stable string for the level+value pair, used to notice that the scope has
 * changed — a different market picked from the navbar dropdown, or an agency
 * session followed by an all-accounts one — and drop the content loaded for the
 * previous one. The slice holds a single copy of each section, so without this
 * the previous scope's content would show while the new fetch is in flight.
 */
export const scopeKey = (scope) =>
    scope?.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY
        ? `country:${scope.countryCode ?? ""}`
        : `agency:${scope?.agencyId ?? ""}`;

/**
 * Request types per level.
 *
 * Both levels point at the same V2 endpoints — now confirmed, not assumed: the
 * level is carried by the payload's `scope` field, not by a different endpoint.
 * If the country level ever gets dedicated endpoints, only the `country` column
 * changes.
 */
const SCOPED_REQUEST_TYPES = {
    getBriefHeroBanner: {
        agency: requestType.getBriefHeroBannerV2,
        country: requestType.getBriefHeroBannerV2,
    },
    setBriefHeroBanner: {
        agency: requestType.setBriefHeroBannerV2,
        country: requestType.setBriefHeroBannerV2,
    },
    getBriefFocusShips: {
        agency: requestType.getBriefFocusShipsV2,
        country: requestType.getBriefFocusShipsV2,
    },
    setBriefFocusShips: {
        agency: requestType.setBriefFocusShipsV2,
        country: requestType.setBriefFocusShipsV2,
    },
    getBriefFocusDestinations: {
        agency: requestType.getBriefFocusDestinationsV2,
        country: requestType.getBriefFocusDestinationsV2,
    },
    setBriefFocusDestinations: {
        agency: requestType.setBriefFocusDestinationsV2,
        country: requestType.setBriefFocusDestinationsV2,
    },
    getBriefCampaign: {
        agency: requestType.getBriefCampaignV2,
        country: requestType.getBriefCampaignV2,
    },
    setBriefCampaign: {
        agency: requestType.setBriefCampaignV2,
        country: requestType.setBriefCampaignV2,
    },
    getAgencyShipDetails: {
        agency: requestType.getAgencyShipDetails,
        country: requestType.getAgencyShipDetails,
    },
    createAgencyShip: {
        agency: requestType.createAgencyShip,
        country: requestType.createAgencyShip,
    },
    updateAgencyShip: {
        agency: requestType.updateAgencyShip,
        country: requestType.updateAgencyShip,
    },
    getAgencyDestinations: {
        agency: requestType.getAgencyDestinations,
        country: requestType.getAgencyDestinations,
    },
    createAgencyDestination: {
        agency: requestType.createAgencyDestination,
        country: requestType.createAgencyDestination,
    },
    updateAgencyDestination: {
        agency: requestType.updateAgencyDestination,
        country: requestType.updateAgencyDestination,
    },
};

/**
 * The request type for an operation at a level.
 *
 * @param {string} operation A key of `SCOPED_REQUEST_TYPES`.
 * @param {string} [level] `CONTENT_MANAGEMENT_LEVELS.*`. Defaults to agency, so a
 *   caller that has no scope to hand keeps the original behaviour.
 */
export const scopedRequestType = (operation, level) => {
    const entry = SCOPED_REQUEST_TYPES[operation];
    if (!entry) throw new Error(`Unknown content management operation "${operation}"`);
    return level === CONTENT_MANAGEMENT_LEVELS.COUNTRY ? entry.country : entry.agency;
};

/**
 * Section names the backend addresses content tables by, for the pre-save check.
 *
 * These are the `section` parameter's only accepted values — anything else is
 * `ERROR_INVALID_CONTENT_SECTION`. They are the backend's own spelling, not the
 * route segments and not the slice's section keys, so the mapping is pinned here
 * rather than derived from either.
 */
export const CONTENT_SECTIONS = {
    HERO_BANNER: "hero_banner",
    FOCUS_SHIPS: "focus_ships",
    FOCUS_DESTINATIONS: "focus_destinations",
    CAMPAIGN: "campaign",
};

/**
 * Whether a market-level save replaces what is already there, per section.
 *
 * Three of the four sections hold at most one record per scope, so a market save
 * is an upsert. Campaigns are the exception: `SET_BRIEF_CAMPAIGN_V2` always
 * INSERTs, so a market save *adds* a market-level banner alongside whatever the
 * agencies already have rather than replacing it. A confirm dialogue that says
 * "this will replace their content" would be wrong for campaigns, and a UI that
 * reuses one copy string for all four sections will say it anyway — hence this
 * table.
 */
export const SECTION_MARKET_SAVE_IS_ADDITIVE = {
    [CONTENT_SECTIONS.HERO_BANNER]: false,
    [CONTENT_SECTIONS.FOCUS_SHIPS]: false,
    [CONTENT_SECTIONS.FOCUS_DESTINATIONS]: false,
    [CONTENT_SECTIONS.CAMPAIGN]: true,
};

/**
 * Payload for `GET_BRIEF_CONTENT_SAVE_PRECHECK_V2` — the figures a market-level
 * save's confirm dialogue needs before it is shown.
 *
 * Call this for any `scope: "market"` save; the response carries
 * `agencies_total`, `agencies_with_own_content` and `agencies_to_receive`
 * (= total − with_own). Agencies that already hold their own agency-level record
 * are *skipped* by default and only taken over when the save passes
 * `overwrite: true`.
 *
 * Note it takes `market_code` + `section` and no `agency_id`, and needs the
 * `full` content tier (bits 128 + 256) — the same tier as the save it precedes,
 * enforced unconditionally, so a user who can reach the dialogue can also
 * complete the save.
 *
 * @param {string} marketCode One of the `COUNTRIES` codes.
 * @param {string} section A `CONTENT_SECTIONS` value.
 */
export const buildSavePrecheckPayload = (marketCode, section) => ({
    data: {
        [MARKET_SCOPE_KEY]: marketCode,
        section,
    },
});

/**
 * The `overwrite` field for a market-level save.
 *
 * Only read when `scope: "market"`. `true` means every agency in the market that
 * holds its own agency-level record has that record snapshotted into
 * `content_management_overwrite_audit` and then deleted, so it falls through to
 * the market value on its next read — i.e. the answer to the confirm dialogue's
 * "also replace the N agencies that have their own content?". Default `false`
 * leaves those agencies untouched.
 *
 * Sent only at market level so an agency save's payload does not carry a field
 * the backend would ignore.
 */
export const buildOverwriteData = (scope, overwrite = false) =>
    scope?.level === CONTENT_MANAGEMENT_LEVELS.COUNTRY ? { overwrite: !!overwrite } : {};
